import {
  Scene,
  Vector3,
  Mesh,
  MeshBuilder,
  StandardMaterial,
  Color3,
  VertexData,
  DynamicTexture,
  Vector2
} from 'babylonjs';

/**
 * Simple seeded random number generator for consistent terrain generation
 */
class SeededRandom {
  private seed: number;

  constructor(seed: number = 12345) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  nextFloat(min: number, max: number): number {
    return min + (max - min) * this.next();
  }
}

/**
 * Simple 2D noise function using seeded random
 */
function noise2D(x: number, z: number, random: SeededRandom, scale: number = 1.0): number {
  // Use a simple hash-based approach for deterministic noise
  const hash = Math.floor(x * 73856093 + z * 19349663) % 1000000;
  const tempRandom = new SeededRandom(hash);
  return tempRandom.nextFloat(-1, 1) * scale;
}

/**
 * Smooth interpolation between values
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Smooth step function for better interpolation
 */
function smoothStep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Bilinear interpolation for smoother noise
 */
function bilinearNoise(
  x: number,
  z: number,
  random: SeededRandom,
  cellSize: number = 10
): number {
  const x0 = Math.floor(x / cellSize) * cellSize;
  const x1 = x0 + cellSize;
  const z0 = Math.floor(z / cellSize) * cellSize;
  const z1 = z0 + cellSize;

  const n00 = noise2D(x0, z0, random, 1.0);
  const n10 = noise2D(x1, z0, random, 1.0);
  const n01 = noise2D(x0, z1, random, 1.0);
  const n11 = noise2D(x1, z1, random, 1.0);

  const tx = smoothStep((x - x0) / cellSize);
  const tz = smoothStep((z - z0) / cellSize);

  const nx0 = lerp(n00, n10, tx);
  const nx1 = lerp(n01, n11, tx);
  return lerp(nx0, nx1, tz);
}

/**
 * Multi-octave noise for more natural terrain (3D version)
 */
function multiOctaveNoise3D(
  x: number,
  y: number,
  z: number,
  random: SeededRandom,
  octaves: number = 3,
  persistence: number = 0.5,
  scale: number = 1.0
): number {
  let value = 0;
  let amplitude = 1;
  let frequency = 1;
  let maxValue = 0;

  for (let i = 0; i < octaves; i++) {
    // Use 3D hash for noise
    const hash = Math.floor(x * frequency * scale * 73856093 + y * frequency * scale * 19349663 + z * frequency * scale * 83492791) % 1000000;
    const tempRandom = new SeededRandom(hash);
    const noise = tempRandom.nextFloat(-1, 1);
    value += noise * amplitude;
    maxValue += amplitude;
    amplitude *= persistence;
    frequency *= 2;
  }

  return value / maxValue;
}

/**
 * Creates a cube with vertices and faces
 */
function createCube(): { vertices: Vector3[], faces: number[][] } {
  const s = 1.0; // Cube size (half-extent)
  const vertices: Vector3[] = [
    new Vector3(-s, -s, -s), // 0
    new Vector3(s, -s, -s), // 1
    new Vector3(s, s, -s), // 2
    new Vector3(-s, s, -s), // 3
    new Vector3(-s, -s, s), // 4
    new Vector3(s, -s, s), // 5
    new Vector3(s, s, s), // 6
    new Vector3(-s, s, s), // 7
  ];

  // Cube faces (each face is two triangles)
  const faces: number[][] = [
    [0, 1, 2, 3], // front
    [5, 4, 7, 6], // back
    [4, 0, 3, 7], // left
    [1, 5, 6, 2], // right
    [3, 2, 6, 7], // top
    [4, 5, 1, 0], // bottom
  ];

  return { vertices, faces };
}

/**
 * Subdivides a quad face into 4 smaller quads
 */
function subdivideFace(
  vertices: Vector3[],
  face: number[],
  vertexMap: Map<string, number>
): number[][] {
  const [v0, v1, v2, v3] = face;
  const p0 = vertices[v0];
  const p1 = vertices[v1];
  const p2 = vertices[v2];
  const p3 = vertices[v3];

  // Get or create midpoints
  const getOrCreateMidpoint = (a: Vector3, b: Vector3): number => {
    const mid = Vector3.Center(a, b);
    const key = `${mid.x.toFixed(6)},${mid.y.toFixed(6)},${mid.z.toFixed(6)}`;
    if (vertexMap.has(key)) {
      return vertexMap.get(key)!;
    }
    const idx = vertices.length;
    vertices.push(mid);
    vertexMap.set(key, idx);
    return idx;
  };

  const m01 = getOrCreateMidpoint(p0, p1); // midpoint of edge 0-1
  const m12 = getOrCreateMidpoint(p1, p2); // midpoint of edge 1-2
  const m23 = getOrCreateMidpoint(p2, p3); // midpoint of edge 2-3
  const m30 = getOrCreateMidpoint(p3, p0); // midpoint of edge 3-0
  const center = getOrCreateMidpoint(
    Vector3.Center(p0, p2),
    Vector3.Center(p1, p3)
  ); // center of face

  // Return 4 subdivided quads
  return [
    [v0, m01, center, m30],
    [m01, v1, m12, center],
    [center, m12, v2, m23],
    [m30, center, m23, v3],
  ];
}

/**
 * Projects a vertex onto a sphere and applies height variation
 */
function projectToSphere(
  vertex: Vector3,
  planetCenter: Vector3,
  planetRadius: number,
  maxHeightVariation: number,
  random: SeededRandom
): Vector3 {
  // Normalize to unit sphere (create new vector to avoid modifying original)
  const normalized = vertex.clone().normalize();

  // Generate height variation using 3D noise based on position
  const noiseScale = 0.01;
  let height = multiOctaveNoise3D(
    normalized.x * 100,
    normalized.y * 100,
    normalized.z * 100,
    random,
    3,
    0.5,
    noiseScale
  );

  // Add larger-scale features
  const largeScale = multiOctaveNoise3D(
    normalized.x * 100,
    normalized.y * 100,
    normalized.z * 100,
    random,
    2,
    0.6,
    noiseScale * 0.3
  ) * 0.6;
  height = height * 0.4 + largeScale;

  // Clamp and scale
  height = Math.max(-1, Math.min(1, height));
  height = height * maxHeightVariation;

  // Project to sphere with height variation
  const radius = planetRadius + height;
  const worldPos = normalized.scale(radius).add(planetCenter);

  return worldPos;
}

/**
 * Creates low-poly terrain using cube subdivision approach that covers the whole sphere
 * 
 * @param scene - The Babylon.js scene
 * @param planetCenter - Center position of the planet (Vector3)
 * @param planetRadius - Radius of the planet (default: 512)
 * @param subdivisions - Number of subdivision levels (default: 3, creates ~384 faces)
 * @param maxHeightVariation - Maximum Y variation in terrain (default: 50)
 * @param seed - Seed for random generation (default: 12345 for consistency)
 * @returns The created terrain mesh
 */
export function createLowPolyTerrain(
  scene: Scene,
  planetCenter: Vector3,
  planetRadius: number = 512,
  subdivisions: number = 3,
  maxHeightVariation: number = 0,
  seed: number = 12345
): Mesh {
  const random = new SeededRandom(seed);

  // Start with a cube
  let { vertices, faces } = createCube();
  const vertexMap = new Map<string, number>();

  // Initialize vertex map
  vertices.forEach((v, i) => {
    const key = `${v.x.toFixed(6)},${v.y.toFixed(6)},${v.z.toFixed(6)}`;
    vertexMap.set(key, i);
  });

  // Subdivide faces multiple times
  for (let level = 0; level < subdivisions; level++) {
    const newFaces: number[][] = [];
    for (const face of faces) {
      const subdivided = subdivideFace(vertices, face, vertexMap);
      newFaces.push(...subdivided);
    }
    faces = newFaces;
  }

  // Project all vertices to sphere with height variation
  const projectedVertices: Vector3[] = [];
  const vertexIndexMap = new Map<number, number>(); // old index -> new index

  for (let i = 0; i < vertices.length; i++) {
    const projected = projectToSphere(vertices[i], planetCenter, planetRadius, maxHeightVariation, random);
    vertexIndexMap.set(i, projectedVertices.length);
    projectedVertices.push(projected);
  }

  // Build positions, indices, and UVs
  const positions: number[] = [];
  const indices: number[] = [];
  const uvs: number[] = [];

  // Add all vertices to positions array
  for (const vertex of projectedVertices) {
    positions.push(vertex.x, vertex.y, vertex.z);

    // Simple UV mapping based on position (can be improved)
    const u = Math.atan2(vertex.x - planetCenter.x, vertex.z - planetCenter.z) / (2 * Math.PI) + 0.5;
    const v = Math.asin((vertex.y - planetCenter.y) / planetRadius) / Math.PI + 0.5;
    uvs.push(u, v);
  }

  // Create indices from faces (each quad = 2 triangles)
  for (const face of faces) {
    const [v0, v1, v2, v3] = face.map(idx => vertexIndexMap.get(idx)!);

    // First triangle
    indices.push(v0, v1, v2);
    // Second triangle
    indices.push(v0, v2, v3);
  }

  // Create mesh
  const terrain = new Mesh("lowPolyTerrain", scene);
  const vertexData = new VertexData();

  vertexData.positions = positions;
  vertexData.indices = indices;
  vertexData.uvs = uvs;

  // Calculate normals
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vertexData.normals = normals;

  vertexData.applyToMesh(terrain);

  // Create material
  const terrainMaterial = new StandardMaterial("terrainMaterial", scene);
  terrainMaterial.specularColor = new Color3(0.1, 0.1, 0.1);
  terrainMaterial.specularPower = 32;
  terrainMaterial.emissiveColor = new Color3(0.02, 0.02, 0.01);

  terrain.material = terrainMaterial;

  console.log(` position counts: ${positions.length / 3}`);

  // Create height-aware texture
  const heightAwareTexture = new DynamicTexture("heightAwareTexture", { width: 256, height: 256 }, scene);
  const heightAwareTextureContext = heightAwareTexture.getContext();
  for (let x = 0; x < 256; x++) {
    for (let y = 0; y < 256; y++) {
      const height = 90 + (y / 256) * 10 + (x / 256) * 10;
      const color = new Color3(height, 100 + (y / 256) * 10, 100 + (x / 256) * 10);
      heightAwareTextureContext.fillStyle = `rgb(${color.r}, ${color.g}, ${color.b})`;
      heightAwareTextureContext.fillRect(x, y, 1, 1);
    }
  }
  heightAwareTexture.update();
  terrainMaterial.diffuseTexture = heightAwareTexture;
  return terrain;
}
